import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { LANG } from "@/types";
import LoginFormView from "@/pages/login/components/view/login-form-view";
import { routes } from "@/consts";
import { Languages } from "lucide-react";
import { Toaster } from "@/app/global-components/toaster";
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  alpha,
  Fade,
  Container,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Login as LoginIcon,
  Business,
  Security,
  TrendingUp,
} from "@mui/icons-material";

const LoginPage: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const theme = useTheme();
  const location = useLocation();

  const getNextLocale = () => {
    return locale === LANG.EN ? LANG.AR : LANG.EN;
  };

  const handleLanguageSwap = () => {
    const pathName = location.pathname;
    const searchParams = location.search;
    if (!pathName) return routes.home;
    const segments = pathName.split("/");
    segments[1] = getNextLocale();
    return segments.join("/") + searchParams;
  };

  const navigate = useNavigate();

  const features = [
    {
      icon: <Business sx={{ color: "#8B5CF6" }} />,
      title: t("authentication.features.businessSolutions"),
      description: t("authentication.features.businessSolutionsDesc"),
    },
    {
      icon: <Security sx={{ color: "#8B5CF6" }} />,
      title: t("authentication.features.securePlatform"),
      description: t("authentication.features.securePlatformDesc"),
    },
    {
      icon: <TrendingUp sx={{ color: "#8B5CF6" }} />,
      title: t("authentication.features.growthOpportunities"),
      description: t("authentication.features.growthOpportunitiesDesc"),
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${alpha(
          "#8B5CF6",
          0.05
        )} 0%, ${alpha("#A855F7", 0.05)} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Container maxWidth="lg">
        <Fade in={true} timeout={800}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
              gap: 4,
              alignItems: "center",
            }}
          >
            {/* Left Side - Features */}
            <Box sx={{ display: { xs: "none", lg: "block" } }}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography
                    variant="h3"
                    fontWeight={700}
                    color="text.primary"
                    gutterBottom
                    sx={{ mb: 3 }}
                  >
                    {t("authentication.welcome.title")}{" "}
                    <Box
                      component="span"
                      sx={{
                        background: `linear-gradient(45deg, #8B5CF6, #A855F7)`,
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      Medex
                    </Box>
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mb: 4, lineHeight: 1.6 }}
                  >
                    {t("authentication.welcome.subtitle")}
                  </Typography>

                  <Box sx={{ space: 3 }}>
                    {features.map((feature, index) => (
                      <Fade in={true} timeout={1200 + index * 200} key={index}>
                        <Card
                          elevation={0}
                          sx={{
                            mb: 2,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor: "background.paper",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              transform: "translateX(8px)",
                              boxShadow: theme.shadows[4],
                            },
                          }}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: "50%",
                                  bgcolor: alpha("#8B5CF6", 0.1),
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {feature.icon}
                              </Box>
                              <Box>
                                <Typography
                                  variant="h6"
                                  fontWeight={600}
                                  color="text.primary"
                                >
                                  {feature.title}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {feature.description}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Fade>
                    ))}
                  </Box>
                </Box>
              </Fade>
            </Box>

            {/* Right Side - Login Form */}
            <Fade in={true} timeout={600}>
              <Card
                elevation={8}
                sx={{
                  borderRadius: 4,
                  boxShadow: "none",
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: "background.paper",
                  maxWidth: 480,
                  mx: "auto",
                  width: "100%",
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  {/* Header */}
                  <Box sx={{ textAlign: "center", mb: 4 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        bgcolor: alpha("#8B5CF6", 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      <LoginIcon sx={{ color: "#8B5CF6", fontSize: 32 }} />
                    </Box>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      color="text.primary"
                      gutterBottom
                    >
                      {t("authentication.login")}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ maxWidth: 300, mx: "auto" }}
                    >
                      {t("authentication.welcome.signInSubtitle")}
                    </Typography>
                  </Box>

                  {/* Login Form */}
                  <LoginFormView />

                  {/* Language Switcher and Register Link */}
                  <Box sx={{ mt: 4 }}>
                    <Divider sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        or
                      </Typography>
                    </Divider>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                      }}
                    >
                      <Tooltip title="Switch Language">
                        <IconButton
                          onClick={() => navigate(handleLanguageSwap())}
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            "&:hover": {
                              bgcolor: alpha("#8B5CF6", 0.1),
                            },
                          }}
                        >
                          <Languages size={20} />
                        </IconButton>
                      </Tooltip>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center" }}
                      >
                        {t("authentication.newAccount")}{" "}
                        <Link
                          to={routes.register}
                          style={{
                            color: "#8B5CF6",
                            textDecoration: "none",
                            fontWeight: 600,
                          }}
                        >
                          {t("authentication.register")}
                        </Link>
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Box>
        </Fade>
      </Container>
      <Toaster />
    </Box>
  );
};

export default LoginPage;
